import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Download, ArrowLeft, MapPin, Phone, Mail, Package, XCircle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import BuyerOrderTracker, { useOrderPolling } from "@/components/OrderTracking";
import OrderStagePill from "@/components/OrderStagePill";
import BankTransferInstructions from "@/components/BankTransferInstructions";
import ReviewButton from "@/components/ReviewButton";
import ReturnRequestDialog from "@/components/returns/ReturnRequestDialog";
import { StatusPill, CountdownChip, fmtDate } from "@/components/returns/ReturnsShared";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useEffect } from "react";

function inrDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [returns, setReturns] = useState([]);
  const [paying, setPaying] = useState(false);

  const fetchReturns = async () => {
    try {
      const { data } = await api.get(`/returns/mine`);
      setReturns((data || []).filter((r) => r.order_id === id));
    } catch { /* silent */ }
  };
  useEffect(() => { fetchReturns(); }, [id]);
  const returnFor = (pid) => returns.find((r) => r.product_id === pid);

  const fetchOrder = async () => {
    try {
      const r = await api.get(`/orders/${id}`);
      setOrder(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? "Order not found" : e.response?.status === 403 ? "You do not have access to this order" : "Could not load order");
    }
  };
  useOrderPolling(fetchOrder, 15000, [id]);

  const cancelOrder = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/orders/${id}/cancel`);
      setOrder(data);
      setConfirmOpen(false);
      toast.success("Order cancelled. Any charged amount will be refunded within 5-7 business days.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel this order");
    } finally { setCancelling(false); }
  };

  const completePayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/orders/${id}/retry-payment`);
      const rzp = data.razorpay_order;
      const verifyAndRefresh = async (res) => {
        const verify = await api.post("/orders/verify-payment", { order_id: id, razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
        if (verify.data.verified) { toast.success("Payment successful"); nav(`/orders/success/${id}`); }
        else { toast.error("Payment verification failed"); fetchOrder(); }
      };
      if (rzp.id.startsWith("order_mock_")) {
        await verifyAndRefresh({ razorpay_order_id: rzp.id, razorpay_payment_id: "pay_mock_" + Date.now(), razorpay_signature: "sig_mock" });
        setPaying(false);
        return;
      }
      if (!window.Razorpay) { toast.error("Razorpay SDK not loaded"); setPaying(false); return; }
      const addr = order?.address || {};
      new window.Razorpay({
        key: data.razorpay_key_id, amount: rzp.amount, currency: "INR", order_id: rzp.id,
        name: "Cityshor LLP", description: "Order payment",
        prefill: { name: addr.full_name, email: addr.email, contact: addr.phone },
        handler: async (res) => { await verifyAndRefresh(res); setPaying(false); },
        modal: { ondismiss: () => { toast.info("Payment not completed. Your order is still Payment pending."); setPaying(false); } },
        theme: { color: "#F48E2E" },
      }).open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the payment");
      fetchOrder();
      setPaying(false);
    }
  };

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/orders/${id}/invoice.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CityShor-Invoice-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download invoice. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center" data-testid="order-detail-error">
        <div className="text-lg text-gray-700">{error}</div>
        <Button className="mt-6 rounded-full bg-[#F48E2E] hover:bg-[#D97815]" onClick={() => nav("/my-orders")}>Back to My Orders</Button>
      </div>
    );
  }
  if (!order) return <div className="mx-auto max-w-3xl px-4 py-16 text-gray-500">Loading order…</div>;

  const shortId = order.id.slice(0, 8).toUpperCase();
  const addr = order.address || {};

  return (
    <div className="mx-auto max-w-4xl px-4 lg:px-8 py-10" data-testid="order-detail-page">
      <button data-testid="back-to-orders" onClick={() => nav("/my-orders")} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#F48E2E] mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to My Orders
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-1">Order Detail</div>
          <h1 className="font-display text-3xl md:text-4xl">Order #{shortId}</h1>
          <div data-testid="order-detail-date" className="text-sm text-gray-500 mt-1">Placed on {inrDate(order.created_at)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {order.payment_method === "online" && order.status === "awaiting_payment" && order.payment_status === "awaiting_payment" && (
            <Button
              data-testid="complete-payment-btn"
              onClick={completePayment}
              disabled={paying}
              className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {paying ? "Opening payment…" : "Complete payment"}
            </Button>
          )}
          <Button
            data-testid="download-invoice-btn"
            onClick={downloadInvoice}
            disabled={downloading}
            variant="outline"
            className="rounded-full border-gray-300"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? "Preparing…" : "Download Tax Invoice"}
          </Button>
          {["awaiting_payment", "awaiting_vendor", "processing", "confirmed", "packed"].includes(order.status) && (
            <Button
              data-testid="cancel-order-btn"
              onClick={() => setConfirmOpen(true)}
              variant="outline"
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <XCircle className="h-4 w-4 mr-2" /> Cancel this order
            </Button>
          )}
        </div>
      </div>

      {/* Live tracker */}
      <div className="p-5 bg-white border border-gray-200 rounded-2xl">
        <OrderStagePill order={order} />
        <div className="mt-4"><BuyerOrderTracker order={order} testIdPrefix="order-detail-track" /></div>
      </div>

      {/* Line items */}
      <div className="mt-5 p-5 bg-white border border-gray-200 rounded-2xl" data-testid="order-detail-items">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-4 w-4 text-[#F48E2E]" />
          <div className="font-medium">Items ({(order.items || []).length})</div>
        </div>
        <div className="divide-y divide-gray-100">
          {(order.items || []).map((i) => (
            <div key={i.product_id} className="flex gap-4 py-3" data-testid={`order-item-${i.product_id}`}>
              {i.image_base64 ? (
                <img src={i.image_base64} alt={i.name} className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-gray-100 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.name}</div>
                <div className="text-xs text-gray-500 mt-0.5 inline-flex items-center flex-wrap gap-1">
                  {i.vendor_business_name && (
                    <span className="inline-flex items-center gap-1">
                      Sold by {i.vendor_business_name}
                      {i.vendor_verified && <VerifiedBadge size="h-3.5 w-3.5" testid={`order-item-verified-${i.product_id}`} />}
                      <span>·</span>
                    </span>
                  )}
                  <span>Qty {i.quantity}</span>
                  {i.size && <span>· Size {i.size}</span>}
                  {i.color && <span>· {i.color}</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">GST {i.gst_slab ?? 0}%</div>
                <ReviewButton
                  orderId={order.id}
                  item={i}
                  canReview={order.status === "delivered"}
                />
                {order.status === "delivered" && (() => {
                  const r = returnFor(i.product_id);
                  if (!r) return (
                    <ReturnRequestDialog orderId={order.id} item={i} onCreated={fetchReturns} />
                  );
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`return-status-${i.product_id}`}>
                      <StatusPill status={r.status} testid={`return-status-pill-${i.product_id}`} />
                      {r.status === "pending_vendor" && <CountdownChip deadlineIso={r.vendor_deadline} testid={`return-clock-${i.product_id}`} prefix="Vendor 48h" />}
                      {r.status === "accepted" && <CountdownChip deadlineIso={r.fulfil_deadline} testid={`return-fulfil-clock-${i.product_id}`} prefix="Fulfil 5d" />}
                      {r.status === "rejected" && r.vendor_note && <span className="text-[11px] text-gray-500">Reason: {r.vendor_note}</span>}
                      {r.status === "completed" && r.completed_at && <span className="text-[11px] text-gray-500">Completed {fmtDate(r.completed_at)}</span>}
                    </div>
                  );
                })()}
              </div>
              <div className="text-right">
                <div className="text-sm">₹{Number(i.price || 0).toLocaleString("en-IN")}</div>
                <div className="text-[11px] text-gray-500">× {i.quantity}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Delivery address */}
        <div className="p-5 bg-white border border-gray-200 rounded-2xl" data-testid="order-detail-address">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-[#F48E2E]" />
            <div className="font-medium">Delivery Address</div>
          </div>
          {addr.full_name && <div className="text-sm font-medium">{addr.full_name}</div>}
          <div className="text-sm text-gray-600 whitespace-pre-line mt-1">
            {[addr.address_line1, addr.address_line2, `${addr.city || ""}${addr.state ? ", " + addr.state : ""}${addr.pincode ? " — " + addr.pincode : ""}`].filter(Boolean).join("\n")}
          </div>
          <div className="mt-3 space-y-1 text-xs text-gray-600">
            {addr.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {addr.phone}</div>}
            {addr.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {addr.email}</div>}
          </div>
        </div>

        {/* Payment summary */}
        <div className="p-5 bg-white border border-gray-200 rounded-2xl" data-testid="order-detail-summary">
          <div className="font-medium mb-3">Payment Summary</div>
          <div className="space-y-1 text-sm">
            {typeof order.subtotal === "number" && (
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>₹{order.subtotal.toLocaleString("en-IN")}</span></div>
            )}
            {(() => {
              const taxes = +((order.product_gst || 0) + (order.packaging_gst || 0) + (order.shipping_gst || 0)).toFixed(2);
              return taxes > 0 ? (
                <div className="flex justify-between text-gray-600"><span>Taxes</span><span>₹{taxes.toLocaleString("en-IN")}</span></div>
              ) : null;
            })()}
            {typeof order.packaging === "number" && (
              <div className="flex justify-between text-gray-600"><span>Packaging</span><span>₹{order.packaging.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.shipping === "number" && (
              <div className="flex justify-between text-gray-600"><span>Shipping{order.shipping_zone ? ` (${order.shipping_zone})` : ""}</span><span>₹{order.shipping.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.platform_fee === "number" && order.platform_fee > 0 && (
              <div className="flex justify-between text-gray-600"><span>Platform fee</span><span>₹{order.platform_fee.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.cod_charges === "number" && order.cod_charges > 0 && (
              <div className="flex justify-between text-gray-600"><span>COD handling</span><span>₹{order.cod_charges.toLocaleString("en-IN")}</span></div>
            )}
            {typeof order.offer_discount === "number" && order.offer_discount > 0 && (
              <div className="flex justify-between text-green-700"><span>Offer{order.offer_code ? ` (${order.offer_code})` : ""}</span><span>−₹{order.offer_discount.toLocaleString("en-IN")}</span></div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <span className="text-sm">Total paid</span>
            <span data-testid="order-detail-total" className="font-display text-xl">₹{Number(order.total || 0).toLocaleString("en-IN")}</span>
          </div>
          <div className="mt-2 text-xs text-gray-500">Payment: {order.payment_method === "cod" ? "Cash on Delivery" : order.payment_method === "bank_transfer" ? "Bank Transfer" : "Online"}</div>
          <BankTransferInstructions order={order} testIdPrefix="order-detail-bank" />
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <Link to="/"><Button variant="outline" className="rounded-full">Continue Shopping</Button></Link>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Cancel this order?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">
              Order #{shortId} · {(order.items || []).length} item(s) · ₹{Number(order.total || 0).toLocaleString("en-IN")}
            </div>
            <p className="text-gray-600">Once cancelled the exhibitor will stop preparing it and stock will be released. Any charged amount is refunded within 5-7 business days. This cannot be undone.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={cancelling} className="rounded-full">Keep order</Button>
              <Button
                data-testid="confirm-cancel-order"
                onClick={cancelOrder}
                disabled={cancelling}
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              >
                {cancelling ? "Cancelling…" : "Yes, cancel order"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
